/**
 * Dimension pivot for the consolidated Performance surface (#6040, T2).
 *
 * The metric-first model keeps one metric block constant (see metrics.ts) and lets
 * the user choose which dimension to group by. This module is the grouping engine:
 * flatten each platform's rows into `PivotLeaf`s, then `pivotLeaves` aggregates them
 * by the chosen dimension into `PivotRow`s that carry the same raw components every
 * unified table derives metrics from. New dimensions (ad, angle, location) plug into
 * `PIVOT_DIMENSIONS` without touching call sites (T3 / #6037).
 */
import type { Platform, PlatformScope } from './types';
import { platformsInScope } from './types';
import { PLATFORM_LABEL } from './platform';

/** One un-grouped record — a Google ad-level row or a Meta campaign-level row. */
export interface PivotLeaf {
  platform: Platform;
  campaignId: string;
  campaignName: string;
  adGroupId?: string | null;
  adGroupName?: string | null;
  adId?: string | null;
  adName?: string | null;
  /** Entity status where known (Meta campaigns); null for Google here. */
  status?: string | null;
  impressions: number;
  clicks: number;
  /** Conversions (Google) or leads (Meta). */
  results: number;
  /** Spend in display currency (already normalized — see money.toDisplayAmount). */
  spend: number;
  /** Conversion value in display currency; null when untracked (Meta) → ROAS "—". */
  conversionValue: number | null;
}

export type PivotDimension = 'platform' | 'campaign' | 'ad-group' | 'ad';

export interface PivotRow {
  key: string;
  label: string;
  platform: Platform;
  resultKind: 'conv' | 'leads';
  status: string | null;
  impressions: number;
  clicks: number;
  results: number;
  spend: number;
  conversionValue: number | null;
}

interface DimensionConfig {
  id: PivotDimension;
  /** Selector label. */
  label: string;
  /** First-column header when grouped by this dimension. */
  columnHeader: string;
  /** Group key; null → the leaf has no value for this dimension and is dropped
   *  (e.g. Meta rows under ad-group grouping). */
  keyOf: (leaf: PivotLeaf) => string | null;
  labelOf: (leaf: PivotLeaf) => string;
  /** Platforms the dimension is meaningful for; gates the selector by scope. */
  platforms: Platform[];
}

export const PIVOT_DIMENSIONS: Record<PivotDimension, DimensionConfig> = {
  platform: {
    id: 'platform',
    label: 'Platform',
    columnHeader: 'Platform',
    keyOf: (l) => l.platform,
    labelOf: (l) => PLATFORM_LABEL[l.platform],
    platforms: ['google', 'meta'],
  },
  campaign: {
    id: 'campaign',
    label: 'Campaign',
    columnHeader: 'Campaign',
    // Prefix with platform: Google campaign_id and Meta meta_campaign_id share a namespace here.
    keyOf: (l) => `${l.platform}:${l.campaignId}`,
    labelOf: (l) => l.campaignName,
    platforms: ['google', 'meta'],
  },
  'ad-group': {
    id: 'ad-group',
    label: 'Ad group',
    columnHeader: 'Ad Group',
    keyOf: (l) => (l.adGroupId != null ? `google:${l.adGroupId}` : null),
    labelOf: (l) => l.adGroupName ?? '—',
    platforms: ['google'],
  },
  ad: {
    id: 'ad',
    label: 'Ad',
    columnHeader: 'Ad',
    keyOf: (l) => (l.adId != null ? `${l.platform}:${l.adId}` : null),
    labelOf: (l) => l.adName ?? l.adId ?? '—',
    platforms: ['google', 'meta'],
  },
};

/**
 * Group leaves by the chosen dimension, summing components. Every group is
 * single-platform (a campaign / ad-group belongs to one platform, and platform-
 * grouping is single by definition), so `platform` and `resultKind` are unambiguous.
 */
export function pivotLeaves(leaves: PivotLeaf[], dimension: PivotDimension): PivotRow[] {
  const cfg = PIVOT_DIMENSIONS[dimension];
  const map = new Map<string, PivotRow>();
  for (const leaf of leaves) {
    const key = cfg.keyOf(leaf);
    if (key == null) continue;
    let row = map.get(key);
    if (!row) {
      row = {
        key,
        label: cfg.labelOf(leaf),
        platform: leaf.platform,
        resultKind: leaf.platform === 'meta' ? 'leads' : 'conv',
        status: leaf.status ?? null,
        impressions: 0,
        clicks: 0,
        results: 0,
        spend: 0,
        conversionValue: null,
      };
      map.set(key, row);
    }
    row.impressions += leaf.impressions;
    row.clicks += leaf.clicks;
    row.results += leaf.results;
    row.spend += leaf.spend;
    if (leaf.conversionValue != null) {
      row.conversionValue = (row.conversionValue ?? 0) + leaf.conversionValue;
    }
  }
  return [...map.values()].sort((a, b) => b.spend - a.spend);
}

/**
 * Filter a panel's candidate dimensions to those worth offering for a scope.
 * Panels pass their own candidates because data granularity differs (the Campaigns
 * panel has campaign-level Meta data; the Creative panel has ad-level Meta data with
 * no campaign linkage). Platform grouping only makes sense across ≥2 platforms; any
 * other dimension needs at least one in-scope platform that supports it (e.g.
 * ad-group disappears under Meta-only scope). Order follows `candidates`.
 */
export function availableDimensions(scope: PlatformScope, candidates: PivotDimension[]): PivotDimension[] {
  const inScope = platformsInScope(scope);
  return candidates.filter((id) => {
    if (id === 'platform') return inScope.length > 1;
    return PIVOT_DIMENSIONS[id].platforms.some((p) => inScope.includes(p));
  });
}
